import { LanguagesEnum } from '@/api/enums';
import { Account } from '@/client/accounts';
import { AccountGoogleWorkspaceFormValues } from '@/client/accounts/types/account-google-workspace.type';
import { SystemRoles } from '@/client/users';
import { LoadingStateType } from '@/common/constants';
import {
  FormDirtyTracker,
  FormikCheckbox,
  FormikInput,
  FormikTextarea,
} from '@/components/form';
import { CopyIconButton } from '@/components/templates/CopyIconButton';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { CheckboxesContainer } from '@/ui/checkboxes-container';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { isDemoRestrictedMode } from '@/utils/helpers';
import { Field, Form, Formik } from 'formik';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { v4 } from 'uuid';
import { UserManagementGroupSelection } from '../UserManagementGroupSelection';
import { GOOGLE_WORKSPACE_FORM_FIELDS } from './constants';
import {
  googleWorkspaceSchema,
  isValidGooglePrivateKey,
} from './validations/google-workspace';

const StyledField = styled(Field)`
  &.p-inputtextarea {
    min-height: 250px !important;
  }
`;

const StyledFileUpload = styled(FileUpload)`
  &.p-fileupload-basic {
    .p-button {
      margin-top: var(--xsmall-padding);
      height: var(--default-input-height);
      color: var(--purple-dark);
      background: var(--white-main);
      border-color: var(--purple-dark);
      font-size: var(--small-font-size);
      line-height: var(--small-line-height);
      border-radius: var(--default-border-radius);

      &:not(button):not(a):not(.p-disabled):hover {
        background: var(--red-dark);
        border-color: var(--red-dark);
        color: var(--white-main);
      }

      .p-button-label {
        font-weight: 500;
        transition: none;
        text-align: left;
      }
    }
  }

  .p-fileupload-choose {
    width: 100%;
  }
`;

const StyledError = styled.p`
  color: var(--red-main);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  margin: 0;
  padding-top: var(--xsmall-padding);
  width: 100%;
`;

type GoogleWorkspaceFormProps = {
  initialValues: AccountGoogleWorkspaceFormValues;
  currentPrivateKey?: string;
  onSubmit: (data: AccountGoogleWorkspaceFormValues) => void;
  onSync: () => void;
  syncState?: LoadingStateType;
  account: Account;
  disabled?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
  submitRef?: React.MutableRefObject<(() => Promise<boolean | void>) | null>;
};

export const GoogleWorkspaceForm: React.FC<GoogleWorkspaceFormProps> = ({
  initialValues,
  currentPrivateKey,
  onSubmit,
  syncState,
  onSync,
  account,
  disabled,
  onDirtyChange,
  submitRef,
}) => {
  const { t } = useTranslation();
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);
  const [isUploading, setIsUploading] = useState(false);
  const fileUploadRef = useRef<FileUpload>(null);

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={googleWorkspaceSchema(
        !!initialValues?.hasClientSecret,
        t,
      )}
      validateOnChange
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        setFieldValue,
        touched,
        setFieldTouched,
        setFieldError,
        validateForm,
      }) => {
        const cantSync =
          JSON.stringify({ ...initialValues, active: null }) !==
          JSON.stringify({ ...values, active: null });

        const handleFileUpload = async (e: FileUploadHandlerEvent) => {
          try {
            const file = e.files[0];
            const reader = new FileReader();

            reader.onload = (event) => {
              const content = event.target?.result as string;
              const jsonData = JSON.parse(content);

              if (!isValidGooglePrivateKey(jsonData)) {
                setFieldError(
                  'jsonPrivateKey',
                  t('error.invalidGooglePrivateKey'),
                );
                return;
              }

              setFieldValue(
                'clientId',
                jsonData[GOOGLE_WORKSPACE_FORM_FIELDS.clientId],
              );
              setFieldValue(
                'clientEmail',
                jsonData[GOOGLE_WORKSPACE_FORM_FIELDS.clientEmail],
              );
              setFieldValue(
                'universeDomain',
                jsonData[GOOGLE_WORKSPACE_FORM_FIELDS.universeDomain],
              );
              setFieldValue(
                'privateKey',
                jsonData[GOOGLE_WORKSPACE_FORM_FIELDS.privateKey],
              );

              setFieldError('jsonPrivateKey', '');
              setIsUploading(false);
              setFieldTouched('jsonPrivateKey', true);

              setTimeout(async () => {
                await validateForm();
              }, 0);

              setFieldError('jsonPrivateKey', '');
              setIsUploading(false);
            };

            reader.readAsText(file);
          } catch (err) {
            setFieldError('jsonPrivateKey', t('error.invalidGooglePrivateKey'));
          } finally {
            fileUploadRef.current?.clear();
          }
        };

        return (
          <FormContainer width={100}>
            <FlexContainer
              gap={40}
              width="100%"
              justify="space-between"
              align="flex-start"
              wrap="wrap"
            >
              <div className="flex-1">
                <Form>
                  <FormDirtyTracker
                    onDirtyChange={onDirtyChange}
                    submitRef={submitRef}
                  />
                  <FlexContainer gap={5}>
                    <div className="field w-full">
                      <StyledFileUpload
                        ref={fileUploadRef}
                        id="jsonPrivateKey"
                        name="jsonPrivateKey"
                        mode="basic"
                        accept=".json"
                        maxFileSize={1000000}
                        customUpload
                        uploadHandler={handleFileUpload}
                        auto
                        chooseLabel={t('google.privateKey.upload')}
                        disabled={disabled || isUploading}
                      />
                      {!!errors.jsonPrivateKey && touched.jsonPrivateKey && (
                        <StyledError>{errors.jsonPrivateKey}</StyledError>
                      )}
                    </div>
                  </FlexContainer>
                  <div className="field w-full">
                    <Field
                      id="clientId"
                      name="clientId"
                      label={t('google.clientId')}
                      className="w-full"
                      component={FormikInput}
                      required
                      disabled={disabled}
                    />
                  </div>
                  <div className="field w-full">
                    <Field
                      id="clientEmail"
                      name="clientEmail"
                      label={t('google.clientEmail')}
                      className="w-full"
                      component={FormikInput}
                      required
                      disabled={disabled}
                    />
                  </div>
                  <div className="field w-full">
                    <Field
                      id="adminEmail"
                      name="adminEmail"
                      label={t('google.adminEmail')}
                      className="w-full"
                      component={FormikInput}
                      required
                      disabled={disabled}
                    />
                  </div>
                  <div className="field w-full">
                    <Field
                      id="universeDomain"
                      name="universeDomain"
                      label={t('google.universeDomain')}
                      className="w-full"
                      component={FormikInput}
                      disabled={disabled}
                    />
                  </div>
                  <FlexContainer gap={5} className="mb-4">
                    <div className="field w-full">
                      <StyledField
                        id="privateKey"
                        name="privateKey"
                        label={t('google.privateKey')}
                        className="w-full"
                        value={values.privateKey}
                        component={FormikTextarea}
                        placeholder={
                          values.hasClientSecret
                            ? t('google.privateKey.set')
                            : undefined
                        }
                        disabled={disabled}
                        required={!values.hasClientSecret}
                        style={{ minHeight: '250px' }}
                        onChange={(
                          e: React.ChangeEvent<HTMLTextAreaElement>,
                        ) => {
                          setFieldValue(
                            'privateKey',
                            e.target.value.replace(/\\n/g, ''),
                          );
                        }}
                      />
                    </div>
                    {currentUser?.role.code === SystemRoles.DEVELOPER &&
                      currentPrivateKey && (
                        <CopyIconButton
                          clipboardValue={currentPrivateKey}
                          className="input-eye-button"
                        />
                      )}
                  </FlexContainer>
                </Form>
              </div>
              <div className="flex-1">
                <CheckboxesContainer>
                  <div className="field-checkbox mb-0" data-id="active">
                    <span id="toggle-wrapper">
                      <Field
                        inputId="active"
                        name="active"
                        label={t('account.google.enable.sync')}
                        value={values.active}
                        component={FormikCheckbox}
                        disabled={disabled}
                      />
                    </span>
                  </div>
                  {values.active && (
                    <>
                      {Object.keys(values.mapping).map((key: string) => (
                        <UserManagementGroupSelection
                          title={t('google.mapGroup', {
                            platformName: account?.platformSettings?.name,
                          })}
                          groupLabel={t('google.groupId')}
                          key={key}
                          index={key}
                          values={values.mapping as any}
                          group={values.mapping[key]}
                          account={account}
                          setFieldValue={setFieldValue}
                          errors={errors}
                          touched={touched as any}
                          setFieldTouched={setFieldTouched}
                          disabled={disabled}
                        />
                      ))}

                      <FlexContainer align="flex-end" justify="space-between">
                        <div>
                          <AppButton
                            icon="pi pi-plus"
                            label={t('azure.newGroup')}
                            severity="secondary"
                            className="white-background"
                            onClick={() =>
                              setFieldValue('mapping', {
                                ...values.mapping,
                                [v4()]: {
                                  groupId: '',
                                  account: account,
                                  branches: [],
                                  groups: [],
                                  language: LanguagesEnum.EN,
                                },
                              })
                            }
                            isDisabled={disabled}
                          />
                        </div>
                        {initialValues.hasGoogleWorkspace && !disabled && (
                          <AppButton
                            icon="pi pi-sync"
                            type="outlined"
                            label={t('azure.force.sync')}
                            className="mt-4 ml-3"
                            onClick={isDemoAccount ? undefined : onSync}
                            state={syncState}
                            isDisabled={cantSync || disabled}
                          />
                        )}
                      </FlexContainer>
                    </>
                  )}
                </CheckboxesContainer>
              </div>
            </FlexContainer>
          </FormContainer>
        );
      }}
    </Formik>
  );
};
